import { buildSteps } from './questions';
import { emptyAnswers, practicalitiesSummary, type BriefBuilderAnswers, type TextStepDef } from './types';

export function toApiAnswers(a: BriefBuilderAnswers) {
  return {
    decision: a.decision,
    territory: a.territory,
    territory_label: a.territoryLabel,
    honest_question: a.honestQuestion,
    people: a.people,
    what_you_know: a.whatYouKnow,
    insider_fact: a.insiderFact,
    territory_q1: a.territoryQ1,
    territory_q2: a.territoryQ2,
    brand_impact: a.brandImpact,
    least_sure: a.leastSure,
    data_held: a.dataHeld,
    ideas: a.ideas,
    ideas_detail: a.ideasDetail,
    optional: a.optional,
    practicalities: {
      budget: a.practicalities.budget,
      budget_unknown: a.practicalities.budgetUnknown,
      decision_date: a.practicalities.decisionDate,
      no_fixed_date: a.practicalities.noFixedDate,
      geography: a.practicalities.geography,
      cross_borders: a.practicalities.crossBorders,
      solidity: a.practicalities.solidity,
      buyer: a.practicalities.buyer,
      locked: a.practicalities.locked,
    },
    unknowns: a.unknowns,
  };
}

export function fromApiAnswers(raw: Record<string, any> | null | undefined): BriefBuilderAnswers {
  const base = emptyAnswers();
  if (!raw || typeof raw !== 'object') return base;
  const p = raw.practicalities || {};
  return {
    ...base,
    decision: raw.decision || '',
    territory: raw.territory || '',
    territoryLabel: raw.territory_label || raw.territoryLabel || '',
    honestQuestion: raw.honest_question || raw.honestQuestion || '',
    people: raw.people || '',
    whatYouKnow: raw.what_you_know || raw.whatYouKnow || '',
    insiderFact: raw.insider_fact || raw.insiderFact || '',
    territoryQ1: raw.territory_q1 || raw.territoryQ1 || '',
    territoryQ2: raw.territory_q2 || raw.territoryQ2 || '',
    brandImpact: raw.brand_impact || raw.brandImpact || '',
    leastSure: raw.least_sure || raw.leastSure || '',
    dataHeld: raw.data_held || raw.dataHeld || '',
    ideas: raw.ideas || '',
    ideasDetail: raw.ideas_detail || raw.ideasDetail || '',
    optional: raw.optional || '',
    practicalities: {
      budget: p.budget || '',
      budgetUnknown: !!(p.budget_unknown ?? p.budgetUnknown),
      decisionDate: p.decision_date || p.decisionDate || '',
      noFixedDate: !!(p.no_fixed_date ?? p.noFixedDate),
      geography: p.geography || '',
      crossBorders: !!(p.cross_borders ?? p.crossBorders),
      solidity: p.solidity || '',
      buyer: p.buyer || '',
      locked: p.locked || '',
    },
    unknowns: Array.isArray(raw.unknowns) ? raw.unknowns : [],
  };
}

export function isPracticalitiesStarted(a: BriefBuilderAnswers): boolean {
  const p = a.practicalities;
  return !!(
    p.budget ||
    p.budgetUnknown ||
    p.decisionDate ||
    p.noFixedDate ||
    (p.geography && p.geography.trim()) ||
    (p.buyer && p.buyer.trim()) ||
    p.crossBorders ||
    p.solidity
  );
}

export function isStepAnswered(step: TextStepDef, answers: BriefBuilderAnswers): boolean {
  if (answers.unknowns.includes(step.id)) return true;
  if (step.optional) return true;
  if (step.kind === 'territory') return !!answers.territory;
  if (step.kind === 'choice') return !!answers.ideas.trim();
  if (step.kind === 'when') return !!answers.practicalities.decisionDate.trim();
  if (step.kind === 'practicalities') return isPracticalitiesStarted(answers);
  const key = step.answerKey as keyof BriefBuilderAnswers;
  const v = answers[key];
  return typeof v === 'string' && v.trim().length > 0;
}

export function stepPreview(step: TextStepDef, answers: BriefBuilderAnswers): string {
  if (answers.unknowns.includes(step.id)) return 'Marked unknown';
  if (step.kind === 'territory') {
    return answers.territoryLabel || answers.territory || '';
  }
  if (step.kind === 'choice') {
    return [answers.ideas, answers.ideasDetail].filter((part) => part.trim()).join(' — ');
  }
  if (step.kind === 'when') {
    return practicalitiesSummary(answers.practicalities);
  }
  if (step.kind === 'practicalities') {
    const bits: string[] = [];
    const p = answers.practicalities;
    if (p.geography?.trim()) bits.push(p.geography.trim());
    if (p.budget) bits.push(p.budget.replace(/_/g, ' '));
    if (p.decisionDate) bits.push(p.decisionDate);
    if (p.solidity) bits.push(p.solidity.replace(/_/g, ' '));
    return bits.join(' · ');
  }
  const key = step.answerKey as keyof BriefBuilderAnswers;
  const v = answers[key];
  return typeof v === 'string' ? v.trim() : '';
}

export function builderProgress(answers: BriefBuilderAnswers): {
  total: number;
  answered: number;
  percent: number;
  steps: TextStepDef[];
  answeredSteps: TextStepDef[];
} {
  const steps = buildSteps(answers);
  const answeredSteps = steps.filter((s) => isStepAnswered(s, answers));
  const answered = answeredSteps.length;
  const total = steps.length;
  return {
    total,
    answered,
    percent: total ? Math.round((answered / total) * 100) : 0,
    steps,
    answeredSteps,
  };
}

/** Short title from the decision answer when the brief was never named. */
export function titleFromDecision(decision: string, maxLen = 72): string {
  const t = (decision || '').replace(/\s+/g, ' ').trim();
  if (!t) return '';
  if (t.length <= maxLen) return t;
  return t.slice(0, maxLen - 1).trimEnd() + '…';
}
